import type { useI18n } from '@hive/i18n';
import { track } from '@hive/track';
import { SettingsIcon } from '@blocksuite/icons/rc';
import { appSettingAtom } from '@toeverything/infra';
import type { createStore } from 'jotai';
import type { useTheme } from 'next-themes';

import type { EditorSettingService } from '../modules/editor-setting';
import { registerHiveCommand } from './registry';

export function registerHiveSettingsCommands({
  t,
  store,
  theme,
  editorSettingService,
}: {
  t: ReturnType<typeof useI18n>;
  store: ReturnType<typeof createStore>;
  theme: ReturnType<typeof useTheme>;
  editorSettingService: EditorSettingService;
}) {
  const unsubs: Array<() => void> = [];
  const updateSettings = editorSettingService.editorSetting.set.bind(
    editorSettingService.editorSetting
  );
  const settings$ = editorSettingService.editorSetting.settings$;

  // color modes
  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-color-mode-to-auto',
      label: `${t['com.hive.cmdk.hive.color-mode.to']()} ${t[
        'com.hive.themeSettings.system'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => theme.theme !== 'system',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'theme',
          value: 'system',
        });
        theme.setTheme('system');
      },
    })
  );
  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-color-mode-to-dark',
      label: `${t['com.hive.cmdk.hive.color-mode.to']()} ${t[
        'com.hive.themeSettings.dark'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => theme.theme !== 'dark',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'theme',
          value: 'dark',
        });
        theme.setTheme('dark');
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-color-mode-to-light',
      label: `${t['com.hive.cmdk.hive.color-mode.to']()} ${t[
        'com.hive.themeSettings.light'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => theme.theme !== 'light',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'theme',
          value: 'light',
        });

        theme.setTheme('light');
      },
    })
  );

  // Font styles
  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-font-style-to-sans',
      label: `${t['com.hive.cmdk.hive.font-style.to']()} ${t[
        'com.hive.appearanceSettings.fontStyle.sans'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => settings$.value.fontFamily !== 'Sans',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'fontStyle',
          value: 'Sans',
        });

        updateSettings('fontFamily', 'Sans');
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-font-style-to-serif',
      label: `${t['com.hive.cmdk.hive.font-style.to']()} ${t[
        'com.hive.appearanceSettings.fontStyle.serif'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => settings$.value.fontFamily !== 'Serif',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'fontStyle',
          value: 'Serif',
        });

        updateSettings('fontFamily', 'Serif');
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: 'hive:change-font-style-to-mono',
      label: `${t['com.hive.cmdk.hive.font-style.to']()} ${t[
        'com.hive.appearanceSettings.fontStyle.mono'
      ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => settings$.value.fontFamily !== 'Mono',
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'fontStyle',
          value: 'Mono',
        });

        updateSettings('fontFamily', 'Mono');
      },
    })
  );

  // Layout Style
  unsubs.push(
    registerHiveCommand({
      id: `hive:change-client-border-style`,
      label: () => `${t['com.hive.cmdk.hive.client-border-style.to']()} ${t[
        store.get(appSettingAtom).clientBorder
          ? 'com.hive.cmdk.hive.switch-state.off'
          : 'com.hive.cmdk.hive.switch-state.on'
      ]()}
        `,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => BUILD_CONFIG.isElectron,
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'clientBorder',
          value: store.get(appSettingAtom).clientBorder ? 'off' : 'on',
        });
        store.set(appSettingAtom, prev => ({
          ...prev,
          clientBorder: !prev.clientBorder,
        }));
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: `hive:change-full-width-layout`,
      label: () =>
        `${t[
          settings$.value.fullWidthLayout
            ? 'com.hive.cmdk.hive.default-page-width-layout.standard'
            : 'com.hive.cmdk.hive.default-page-width-layout.full-width'
        ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'fullWidthLayout',
          value: settings$.value.fullWidthLayout ? 'off' : 'on',
        });
        updateSettings('fullWidthLayout', !settings$.value.fullWidthLayout);
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: `hive:change-noise-background-on-the-sidebar`,
      label: () =>
        `${t[
          'com.hive.cmdk.hive.noise-background-on-the-sidebar.to'
        ]()} ${t[
          store.get(appSettingAtom).enableNoisyBackground
            ? 'com.hive.cmdk.hive.switch-state.off'
            : 'com.hive.cmdk.hive.switch-state.on'
        ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () => BUILD_CONFIG.isElectron,
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'enableNoisyBackground',
          value: store.get(appSettingAtom).enableNoisyBackground ? 'off' : 'on',
        });

        store.set(appSettingAtom, prev => ({
          ...prev,
          enableNoisyBackground: !prev.enableNoisyBackground,
        }));
      },
    })
  );

  unsubs.push(
    registerHiveCommand({
      id: `hive:change-translucent-ui-on-the-sidebar`,
      label: () =>
        `${t['com.hive.cmdk.hive.translucent-ui-on-the-sidebar.to']()} ${t[
          store.get(appSettingAtom).enableBlurBackground
            ? 'com.hive.cmdk.hive.switch-state.off'
            : 'com.hive.cmdk.hive.switch-state.on'
        ]()}`,
      category: 'hive:settings',
      icon: <SettingsIcon />,
      preconditionStrategy: () =>
        BUILD_CONFIG.isElectron && environment.isMacOs,
      run() {
        track.$.cmdk.settings.changeAppSetting({
          key: 'enableBlurBackground',
          value: store.get(appSettingAtom).enableBlurBackground ? 'off' : 'on',
        });
        store.set(appSettingAtom, prev => ({
          ...prev,
          enableBlurBackground: !prev.enableBlurBackground,
        }));
      },
    })
  );

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
